'use client';

/**
 * CostMonthlyBreakdownCard — mode6's backward-looking decomposition.
 *
 * The complement to the forecast card: instead of "where will spend
 * go" (forward-looking trajectory bands), this answers "where has
 * spend been and what's the model mix" (backward-looking by-model
 * stacked bars), with forward MC projection at the observed model
 * mix continuing through the horizon.
 *
 * Layout: stacked Bars per :Model on the left (tokens) + Line on
 * the right (CHF P50) + Area ribbon for the P5–P95 cost band over
 * the projected portion. Vertical reference line marks the
 * historical / projected transition.
 *
 * Reads `cost_monthly_breakdown` via the capability matrix —
 * (category=cost_analytics, action=monthly_breakdown).
 *
 * @see klyde_edge/klyde_costs/responses.py — CostMonthlyBreakdownResponse
 * @see klyde_edge/ui_config/composition/modes/mode6.py — mode6_breakdown preset
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useMemo, useRef, useState } from 'react';
import { Loader2, XCircle, BarChart3, Download, Users } from 'lucide-react';
import {
  Area, Bar, ComposedChart, Line, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis, CartesianGrid, Legend, Cell,
} from 'recharts';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useFinopsLabel, getFinopsLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { useFinopsStrategyData } from './use-finops-query';
import { cn } from '@/lib/utils/cn';
import { exportCardAsPng, exportCardAsSvg, todayStamp } from '@/lib/utils/export-chart';
import type {
  CostMonthlyBreakdownResponse,
  CostMonthlyBreakdown,
  CostModelMonthly,
} from '@/lib/api/types';

// ─────────────────────────────────────────────────────────────────────
// Family colour palettes — distinct shades per sub-model so the
// stacked bar reads as "family A grouped together, family B grouped
// together, fixed at the bottom". Keeps the model-mix story visible
// even when models multiply.
// ─────────────────────────────────────────────────────────────────────

const FAMILY_PALETTES: Record<string, string[]> = {
  gemini:           ['#4338ca', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe'], // indigo ramp
  vertex_embedding: ['#b45309', '#d97706', '#f59e0b', '#fbbf24', '#fcd34d'], // amber ramp
};
const FAMILY_FALLBACK = ['#475569', '#64748b', '#94a3b8', '#cbd5e1']; // slate fallback

const COST_LINE_COLOR = '#10b981';     // emerald
const COST_BAND_COLOR = '#10b981';     // emerald (low-opacity fill)

// ─────────────────────────────────────────────────────────────────────
// Card
// ─────────────────────────────────────────────────────────────────────

export function CostMonthlyBreakdownCard() {
  const breakdownStrategy = useCapabilityStore(
    (s) => s.strategyName('cost_analytics', 'monthly_breakdown'),
  );

  // User-scaling lever. Same semantics as the other finops cards —
  // null = use substrate-resolved n_users_observed; any other value
  // triggers per-user CLT scaling on variable models + fixed ×3.
  // Default 500 — projects at-scale by default to match the runway
  // and forecast-burn cards. Reception surfaces these to outside
  // visitors who care about post-PMF economics, not today's small
  // observed count.
  const [usersProjected, setUsersProjected] = useState<number | null>(500);

  // Card-root ref for PNG/SVG export.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const handleExportPng = () => {
    exportCardAsPng(cardRef.current, `klyde-token-breakdown-${todayStamp()}.png`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };
  const handleExportSvg = () => {
    exportCardAsSvg(cardRef.current, `klyde-token-breakdown-${todayStamp()}.svg`)
      .catch(() => alert(getFinopsLabel('export_failed')));
  };

  // Simulation params — one object, because it's ALSO the query-key
  // tail: the capture envelope records this card's payload under
  // ['strategy-data', <strategy>, simParams].
  const simParams = {
    horizon_months: 12,
    window_days: 180,
    n_simulations: 5_000,
    seed: 42,
    ...(usersProjected != null ? { n_users_projected: usersProjected } : {}),
  };
  const simQuery = useFinopsStrategyData<CostMonthlyBreakdownResponse>(breakdownStrategy, simParams);
  const data = simQuery.data?.success ? simQuery.data.data ?? null : null;
  const error = simQuery.data && !simQuery.data.success
    ? simQuery.data.error || 'Failed to load breakdown'
    : simQuery.error
      ? simQuery.error.message ?? 'Network error'
      : null;
  const isLoading = simQuery.isFetching;

  // i18n chrome — all hooks before early returns.
  const loadingLabel = useFinopsLabel('breakdown_loading');
  const emptyLabel = useFinopsLabel('breakdown_empty');
  const yLabelTokens = useFinopsLabel('breakdown_yaxis_tokens');
  const yLabelChf = useFinopsLabel('breakdown_yaxis_chf');
  const transitionLabel = useFinopsLabel('breakdown_transition_label');
  const legendChfP50 = useFinopsLabel('breakdown_legend_chf_p50');
  const legendChfBand = useFinopsLabel('breakdown_legend_chf_band');
  const caveatLabel = useFinopsLabel('breakdown_caveat');

  // Derive the union of models across all months + a stable colour
  // assignment. Keeps the legend / stack order consistent across
  // re-renders and across history → projection.
  const modelOrder = useMemo(() => buildModelOrder(data), [data]);

  // Long-form chart rows: one per month, with token columns per model
  // + cost columns for the line and band.
  const chartData = useMemo(
    () => buildChartRows(data, modelOrder),
    [data, modelOrder],
  );

  // Where does the historical → projected split sit? Used for the
  // transition reference line.
  const transitionMonth = useMemo(() => {
    if (!data) return null;
    const firstProjected = data.months.find((m) => !m.is_historical);
    return firstProjected?.month ?? null;
  }, [data]);

  // Scene-capture registration — WHERE comes from the container's
  // CardPlacementProvider; this declares the WHAT: painted breakdown
  // summary, the user-scaling lever as state, and the query_key that
  // lets the capture envelope record the raw MC response.
  useCardExport({
    label: 'Monthly breakdown',
    content_key: 'cost_monthly_breakdown_canvas',
    text: data
      ? `Monthly token/cost breakdown: ${data.months.length} months ` +
        `(${data.months.filter((m) => m.is_historical).length} historical, ` +
        `${data.months.filter((m) => !m.is_historical).length} projected) · ` +
        `${modelOrder.length} models · ${data.n_users_projected} users projected`
      : 'Monthly token/cost breakdown — no simulation data',
    // PAINTED values (capture rule 3) — raw numerics live in the payload.
    json: data
      ? {
          months: String(data.months.length),
          transition_month: transitionMonth,
          models: String(modelOrder.length),
          simulations: data.n_simulations.toLocaleString(),
          users_projected: String(data.n_users_projected),
        }
      : undefined,
    state: { users_projected: usersProjected },
    status: error ? 'error' : data ? 'loaded' : isLoading ? 'loading' : 'idle',
    error: error ?? undefined,
    // From the hook, not reconstructed (capture rule 1 — no drift).
    query_key: breakdownStrategy ? simQuery.queryKey : null,
  });

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {loadingLabel}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={text.body}>{error}</p>
      </div>
    );
  }
  if (!data || modelOrder.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <BarChart3 className="h-8 w-8 mb-2 opacity-40" />
        <p className={text.body}>{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div ref={cardRef} className="flex flex-col h-full p-4 gap-3 overflow-hidden bg-background">
      <Header data={data} onExportPng={handleExportPng} onExportSvg={handleExportSvg} />

      <UserScaleControl
        observed={data.n_users_observed}
        projected={data.n_users_projected}
        selected={usersProjected}
        onChange={setUsersProjected}
      />

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 64, bottom: 8, left: 8 }}>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11 }}
              tickFormatter={monthLabel}
              interval="preserveStartEnd"
            />
            {/* Left Y: tokens (millions). Format with M / K suffix. */}
            <YAxis
              yAxisId="tokens"
              tick={{ fontSize: 11 }}
              tickFormatter={formatTokens}
              width={56}
              label={{ value: yLabelTokens, angle: -90, position: 'insideLeft', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            />
            {/* Right Y: CHF — monthly cost. */}
            <YAxis
              yAxisId="chf"
              orientation="right"
              tick={{ fontSize: 11 }}
              tickFormatter={(v: number) => `${v.toFixed(0)}`}
              width={48}
              label={{ value: yLabelChf, angle: 90, position: 'insideRight', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip
              labelFormatter={(m) => monthLabelLong(String(m))}
              formatter={(v: number, n: string) => {
                if (n === legendChfP50) return [`CHF ${v.toFixed(2)}`, n];
                if (n === legendChfBand) return null;  // hide the helper-stack legend
                return [formatTokens(v), n];
              }}
              contentStyle={{ fontSize: 12 }}
            />

            {/* Historical / projected transition marker */}
            {transitionMonth && (
              <ReferenceLine
                xAxisId={0}
                x={transitionMonth}
                yAxisId="tokens"
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="3 3"
                label={{ value: transitionLabel, position: 'top', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              />
            )}

            {/* Stacked Bars per model — order locked by `modelOrder` so
                the stack is consistent across months. Cell-coloured
                so projected bars can render with reduced opacity. */}
            {modelOrder.map((m) => (
              <Bar
                key={m.model_id}
                yAxisId="tokens"
                dataKey={`tokens__${m.model_id}`}
                stackId="tokens"
                name={m.model_id}
                fill={m.color}
                isAnimationActive={false}
              >
                {chartData.map((row) => (
                  <Cell
                    key={`${m.model_id}-${row.month}`}
                    fill={m.color}
                    fillOpacity={row.is_historical ? 1.0 : 0.55}
                  />
                ))}
              </Bar>
            ))}

            {/* P5–P95 cost-band envelope. Two stacked Areas:
                  base = p5 (transparent),
                  delta = p95 - p5 (filled with low opacity).
                Historical months emit p95_delta = 0 → the band
                naturally collapses on actuals. */}
            <Area
              yAxisId="chf"
              type="monotone"
              dataKey="chf_p5"
              stackId="cost-band"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
              name={legendChfBand}
            />
            <Area
              yAxisId="chf"
              type="monotone"
              dataKey="chf_p95_delta"
              stackId="cost-band"
              stroke="none"
              fill={COST_BAND_COLOR}
              fillOpacity={0.18}
              isAnimationActive={false}
              legendType="none"
              name={legendChfBand}
            />

            {/* Cost line — P50 monthly CHF, both historical (where p5
                = p50 = p95) and projected portions. */}
            <Line
              yAxisId="chf"
              type="monotone"
              dataKey="chf_p50"
              name={legendChfP50}
              stroke={COST_LINE_COLOR}
              strokeWidth={2.5}
              dot={{ r: 3, fill: COST_LINE_COLOR }}
              isAnimationActive={false}
            />

            <Legend wrapperStyle={{ fontSize: 11 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[11px] text-foreground/70">{caveatLabel}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Header — title + provenance + Export buttons
// ─────────────────────────────────────────────────────────────────────

function Header({
  data,
  onExportPng,
  onExportSvg,
}: {
  data: CostMonthlyBreakdownResponse;
  onExportPng: () => void;
  onExportSvg: () => void;
}) {
  const title = useFinopsLabel('breakdown_title');
  const provenance = useFinopsLabel('breakdown_provenance', { n_obs: data.n_observations });
  const exportPngLabel = useFinopsLabel('export_png_label');
  const exportPngTooltip = useFinopsLabel('export_png_tooltip');
  const exportSvgLabel = useFinopsLabel('export_svg_label');
  const exportSvgTooltip = useFinopsLabel('export_svg_tooltip');
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div>
        <div className={cn(text.heading, 'flex items-center gap-2')}>
          <BarChart3 className="h-4 w-4 text-primary" />
          {title}
        </div>
        <div className="text-xs text-foreground/90 mt-0.5">{provenance}</div>
      </div>
      <div className={cn(text.muted, 'shrink-0')}>
        <div className="flex items-center gap-1" data-export-hidden="true">
          <button
            onClick={onExportPng}
            title={exportPngTooltip}
            aria-label={exportPngTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportPngLabel}</span>
          </button>
          <button
            onClick={onExportSvg}
            title={exportSvgTooltip}
            aria-label={exportSvgTooltip}
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-card text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors text-[11px]"
          >
            <Download className="h-3 w-3" />
            <span>{exportSvgLabel}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// UserScaleControl — same chip ladder pattern as the other finops cards
// ─────────────────────────────────────────────────────────────────────

function UserScaleControl({
  observed,
  projected,
  selected,
  onChange,
}: {
  observed: number;
  projected: number;
  selected: number | null;
  onChange: (v: number | null) => void;
}) {
  const baseline = Math.max(observed, 1);
  const scaleLabel = useFinopsLabel('scale_label', { observed: baseline });
  const varianceHint = useFinopsLabel('scale_variance_hint');
  const fixedUpliftHint = useFinopsLabel('scale_fixed_uplift_note', { factor: 3 });
  const FORWARD_TARGETS = [50, 100, 500, 1000];
  const targets: Array<{ count: number; value: number | null; isToday: boolean }> = [
    { count: baseline, value: null, isToday: true },
    ...FORWARD_TARGETS.filter((n) => n > baseline).map((n) => ({ count: n, value: n, isToday: false })),
  ];
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-foreground/80 flex items-center gap-1">
        <Users className="h-3 w-3" />
        {scaleLabel}
      </span>
      {targets.map((t) => (
        <UserScaleChip
          key={t.count}
          count={t.count}
          value={t.value}
          baseline={baseline}
          isToday={t.isToday}
          on={(selected ?? null) === t.value}
          onSelect={() => onChange(t.value)}
        />
      ))}
      {projected !== baseline && (
        <span className="text-foreground/70 text-[11px]">{varianceHint}{fixedUpliftHint}</span>
      )}
    </div>
  );
}

function UserScaleChip({
  count,
  value,
  baseline,
  isToday,
  on,
  onSelect,
}: {
  count: number;
  value: number | null;
  baseline: number;
  isToday: boolean;
  on: boolean;
  onSelect: () => void;
}) {
  const todayLabel   = useFinopsLabel('scale_chip_today', { count });
  const forwardLabel = useFinopsLabel('scale_chip',       { count });
  const label = isToday ? todayLabel : forwardLabel;
  const tooltip = useFinopsLabel('scale_chip_tooltip', { count, baseline });
  return (
    <button
      onClick={onSelect}
      className={cn(
        'px-2 py-1 rounded border transition-colors',
        on
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'border-border bg-card text-foreground/80 hover:border-primary/50',
      )}
      title={tooltip}
    >
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pure helpers
// ─────────────────────────────────────────────────────────────────────

interface ModelOrderEntry {
  model_id: string;
  family: string;
  color: string;
}

/** Stable model order across the chart's lifetime — sorted by family
 *  + model_id, with each family pulling colours from its palette in
 *  order. Drives both the bar stack order and the legend. */
function buildModelOrder(
  data: CostMonthlyBreakdownResponse | null,
): ModelOrderEntry[] {
  if (!data) return [];
  const seen = new Map<string, string>(); // model_id → family
  for (const month of data.months) {
    for (const m of month.models) {
      if (!seen.has(m.model_id)) seen.set(m.model_id, m.family || 'unknown');
    }
  }
  const entries = [...seen.entries()].map(([model_id, family]) => ({ model_id, family }));
  entries.sort((a, b) => (a.family + a.model_id).localeCompare(b.family + b.model_id));

  const familyCounts: Record<string, number> = {};
  return entries.map((e) => {
    familyCounts[e.family] = (familyCounts[e.family] ?? 0);
    const palette = FAMILY_PALETTES[e.family] ?? FAMILY_FALLBACK;
    const color = palette[familyCounts[e.family] % palette.length];
    familyCounts[e.family] += 1;
    return { ...e, color };
  });
}

interface ChartRow {
  month: string;
  is_historical: boolean;
  chf_p5: number;
  chf_p50: number;
  chf_p95_delta: number;
  [tokenKey: string]: string | number | boolean;
}

/** Recharts wants flat rows. Project each month to:
 *    tokens__<model_id> = month tokens for that model (0 if absent),
 *    chf_p5             = monthly cost P5,
 *    chf_p50            = monthly cost P50,
 *    chf_p95_delta      = max(P95 - P5, 0) — stacked on top of p5
 *                         to draw the band; zero on historical rows.
 */
function buildChartRows(
  data: CostMonthlyBreakdownResponse | null,
  modelOrder: ModelOrderEntry[],
): ChartRow[] {
  if (!data) return [];
  return data.months.map((month) => {
    const modelTokens: Record<string, number> = {};
    for (const m of month.models) modelTokens[m.model_id] = m.tokens;
    const row: ChartRow = {
      month: month.month,
      is_historical: month.is_historical,
      chf_p5: month.chf_p5,
      chf_p50: month.chf_p50,
      chf_p95_delta: Math.max(month.chf_p95 - month.chf_p5, 0),
    };
    for (const m of modelOrder) {
      row[`tokens__${m.model_id}`] = modelTokens[m.model_id] ?? 0;
    }
    return row;
  });
}

function formatTokens(v: number): string {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000)     return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)         return `${(v / 1_000).toFixed(1)}K`;
  return `${v.toFixed(0)}`;
}

function monthLabel(ym: string): string {
  // 2026-04 → Apr
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short' });
}

function monthLabelLong(ym: string): string {
  // 2026-04 → April 2026
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
